// ── swipe.js ──────────────────────────────────────────────────────────────────
// Pure horizontal-swipe gesture math for row actions (swipe-to-remove / dismiss).
// Every decision is a pure function of touch coordinates — NO DOM, NO refs — so
// the gesture behavior is unit-tested (swipe.test.js) independently of React.
// SwipeRow.jsx is the thin React shell that feeds touch events through these.

export const SWIPE = {
  threshold: 84,   // px of horizontal travel (at release) that COMMITS the action
  maxTravel: 104,  // px the row is allowed to slide (clamp for the reveal)
  ratio: 1.2,      // |dx| must exceed |dy| * ratio to count as a horizontal swipe
  lockSlop: 10,    // px of vertical drift (before lock) that hands off to scroll
}

// Is the gesture primarily horizontal? Until a drag proves horizontal we let the
// browser scroll vertically — this is what keeps swipe-rows from fighting the list.
export function isHorizontalSwipe(dx, dy, ratio = SWIPE.ratio) {
  return Math.abs(dx) > Math.abs(dy) * ratio && Math.abs(dx) > 0
}

// Clamp raw dx to the allowed drag range for a direction:
//   'left'  → row slides negative-x only (action revealed on the trailing edge)
//   'right' → row slides positive-x only
//   'both'  → symmetric
export function clampTravel(dx, direction = 'left', maxTravel = SWIPE.maxTravel) {
  if (direction === 'left')  return Math.max(-maxTravel, Math.min(0, dx))
  if (direction === 'right') return Math.min(maxTravel,  Math.max(0, dx))
  return Math.max(-maxTravel, Math.min(maxTravel, dx))
}

// 0..1 — how close the current travel is to committing, in the allowed direction.
// Drives the reveal's opacity/scale. Travel opposite the allowed direction → 0.
export function swipeProgress(dx, direction = 'left', threshold = SWIPE.threshold) {
  const t = direction === 'left' ? -dx : direction === 'right' ? dx : Math.abs(dx)
  if (t <= 0) return 0
  return Math.min(1, t / threshold)
}

// On release: has the swipe crossed the commit threshold in the allowed direction?
export function shouldCommit(dx, direction = 'left', threshold = SWIPE.threshold) {
  if (direction === 'left')  return dx <= -threshold
  if (direction === 'right') return dx >=  threshold
  return Math.abs(dx) >= threshold
}

// Reveal-and-settle model (iOS-Mail style, used by SwipeRow): on release, snap
// the row to the nearest resting position — fully open (±actionWidth) once dragged
// past `openRatio` of the action width, otherwise closed (0). Symmetric: negative
// travel settles to the left action, positive to the right.
export function settleTarget(dx, actionWidth, openRatio = 0.5) {
  const th = actionWidth * openRatio
  if (dx <= -th) return -actionWidth
  if (dx >=  th) return  actionWidth
  return 0
}
